import type { ButtonHTMLAttributes, FC, ReactNode } from 'react'

import { LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

type ButtonVariant = 'fire' | 'dark' | 'ghost' | 'danger' | 'success'
type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant
    size?: ButtonSize
    loading?: boolean
}

// Chunky block buttons with a pressed-down bottom edge, like an in-game button.
const variantClasses: Record<ButtonVariant, string> = {
    fire: 'bg-linear-to-b from-primary-light to-primary text-primary-foreground shadow-[0_3px_0_0_var(--color-primary-dark),inset_0_1px_0_rgb(255_255_255/0.35)] hover:brightness-105 active:translate-y-[2px] active:shadow-[0_1px_0_0_var(--color-primary-dark)]',
    dark: 'border border-border bg-secondary text-foreground shadow-[0_3px_0_0_#0a0603] hover:bg-accent active:translate-y-[2px] active:shadow-[0_1px_0_0_#0a0603]',
    ghost: 'text-muted-foreground hover:bg-accent hover:text-foreground',
    danger: 'bg-linear-to-b from-[#ff7062] to-[#e2392b] text-white shadow-[0_3px_0_0_#8f1d14,inset_0_1px_0_rgb(255_255_255/0.25)] hover:brightness-105 active:translate-y-[2px] active:shadow-[0_1px_0_0_#8f1d14]',
    success: 'bg-linear-to-b from-[#62f0a0] to-[#2fc46f] text-[#062615] shadow-[0_3px_0_0_#167a40,inset_0_1px_0_rgb(255_255_255/0.3)] hover:brightness-105 active:translate-y-[2px] active:shadow-[0_1px_0_0_#167a40]'
}

const sizeClasses: Record<ButtonSize, string> = {
    sm: 'h-8 gap-1.5 rounded-md px-3 text-sm',
    md: 'h-10 gap-2 rounded-lg px-4 text-sm',
    lg: 'h-12 gap-2 rounded-lg px-6 text-base'
}

// Also used to style links that should look like buttons.
export function buttonClasses(variant: ButtonVariant = 'fire', size: ButtonSize = 'md', className?: string): string {
    return cn(
        'inline-flex select-none items-center justify-center font-bold whitespace-nowrap transition-[filter,transform,background-color,box-shadow] duration-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-55',
        variantClasses[variant],
        sizeClasses[size],
        className
    )
}

export const Button: FC<ButtonProps> = ({
    variant = 'fire',
    size = 'md',
    loading = false,
    disabled,
    className,
    children,
    type = 'button',
    ...rest
}): ReactNode => {
    return (
        <button
            type={type}
            disabled={disabled || loading}
            className={buttonClasses(variant, size, className)}
            {...rest}
        >
            {loading && <LoaderCircle className='size-4 animate-spin' aria-hidden='true' />}
            {children}
        </button>
    )
}
